import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  Badge,
  BlockStack,
  InlineStack,
  Text,
  SkeletonBodyText,
  Box,
  Divider,
  DataTable,
  Button,
  TextField,
  Tabs,
  Checkbox,
} from '@shopify/polaris';
import { clinicsApi, doctorsApi } from '@/services';
import type { Rental, RentalStatus } from '@/types';
import { orderNumber } from '@/utils/orderNumber';

export default function ClinicDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    isConsignmentClinic: false,
    shipAddress1: '',
    shipAddress2: '',
    shipCity: '',
    shipProvince: '',
    shipZip: '',
    billAddress1: '',
    billAddress2: '',
    billCity: '',
    billProvince: '',
    billZip: '',
  });

  const { data: clinic, isLoading: clinicLoading } = useQuery({
    queryKey: ['clinic', id],
    queryFn: () => clinicsApi.getById(id!),
    enabled: !!id,
  });

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['clinic-stats', id],
    queryFn: () => clinicsApi.getStats(id!),
    enabled: !!id,
  });

  const { data: rentals, isLoading: rentalsLoading } = useQuery({
    queryKey: ['clinic-rentals', id],
    queryFn: () => clinicsApi.getRentals(id!, { limit: 10 }),
    enabled: !!id && selectedTab === 1,
  });

  const { data: doctorsData, isLoading: doctorsLoading } = useQuery({
    queryKey: ['clinic-doctors', id],
    queryFn: () => doctorsApi.list({ clinicId: id!, limit: 100 }),
    enabled: !!id && selectedTab === 2,
  });

  const updateMutation = useMutation({
    mutationFn: (data: typeof editForm) =>
      clinicsApi.update(id!, {
        name: data.name,
        email: data.email,
        phone: data.phone,
        isConsignmentClinic: data.isConsignmentClinic,
        shippingAddress: data.shipAddress1
          ? {
              address1: data.shipAddress1,
              address2: data.shipAddress2 || undefined,
              city: data.shipCity,
              province: data.shipProvince,
              zip: data.shipZip,
              countryCode: 'CA',
            }
          : undefined,
        billingAddress: data.billAddress1
          ? {
              address1: data.billAddress1,
              address2: data.billAddress2 || undefined,
              city: data.billCity,
              province: data.billProvince,
              zip: data.billZip,
              countryCode: 'CA',
            }
          : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic', id] });
      setIsEditing(false);
    },
  });

  const getStatusBadge = (status: RentalStatus) => {
    const statusConfig: Record<string, { tone: 'success' | 'warning' | 'critical' | 'info' | undefined }> = {
      pending: { tone: undefined },
      processing: { tone: 'info' },
      shipped: { tone: 'info' },
      in_use: { tone: 'success' },
      returned: { tone: 'warning' },
      complete: { tone: 'success' },
      cancelled: { tone: 'critical' },
    };
    const config = statusConfig[status] || { tone: undefined };
    return <Badge tone={config.tone}>{status.replace('_', ' ')}</Badge>;
  };

  if (clinicLoading) {
    return (
      <Page title="Clinic Details">
        <Card>
          <SkeletonBodyText lines={10} />
        </Card>
      </Page>
    );
  }

  if (!clinic) {
    return (
      <Page
        title="Clinic Not Found"
        backAction={{ content: 'Back', onAction: () => navigate('/clinics') }}
      >
        <Card>
          <Text as="p">The requested clinic could not be found.</Text>
        </Card>
      </Page>
    );
  }

  const rentalRows = (rentals?.data || []).map((rental: Rental) => [
    <Button key={rental.id} variant="plain" onClick={() => navigate(`/rentals/${rental.id}`)}>
      {orderNumber(rental)}
    </Button>,
    rental.patientName || 'N/A',
    rental.rentalStartDate ? new Date(rental.rentalStartDate).toLocaleDateString() : 'N/A',
    rental.rentalEndDate ? new Date(rental.rentalEndDate).toLocaleDateString() : 'N/A',
    `$${Number(rental.totalAmount).toFixed(2)}`,
    getStatusBadge(rental.rentalStatus),
  ]);

  const doctorRows = (doctorsData?.data || []).map((doctor) => [
    <Button key={doctor.id} variant="plain" onClick={() => navigate(`/doctors/${doctor.id}`)}>
      {`Dr. ${doctor.firstName} ${doctor.lastName}`}
    </Button>,
    doctor.email || 'N/A',
    doctor.phone || 'N/A',
    doctor.status === 'active' ? (
      <Badge key={`${doctor.id}-status`} tone="success">Active</Badge>
    ) : (
      <Badge key={`${doctor.id}-status`}>{doctor.status}</Badge>
    ),
  ]);

  const tabs = [
    { id: 'details', content: 'Details', accessibilityLabel: 'Clinic details' },
    { id: 'rentals', content: 'Rentals', accessibilityLabel: 'Clinic rentals' },
    { id: 'doctors', content: 'Doctors', accessibilityLabel: 'Associated doctors' },
  ];

  return (
    <Page
      title={clinic.name}
      backAction={{ content: 'Back', onAction: () => navigate('/clinics') }}
      titleMetadata={
        <Badge tone={clinic.status === 'active' ? 'success' : undefined}>
          {clinic.status}
        </Badge>
      }
      primaryAction={{
        content: 'Create Order on Behalf',
        onAction: () => navigate(`/clinics/${id}/create-order`),
      }}
      secondaryActions={[
        {
          content: isEditing ? 'Cancel' : 'Edit',
          onAction: () => {
            if (isEditing) {
              setIsEditing(false);
            } else {
              setEditForm({
                name: clinic.name,
                email: clinic.email || '',
                phone: clinic.phone || '',
                isConsignmentClinic: (clinic as any).isConsignmentClinic || false,
                shipAddress1: clinic.shippingAddress?.address1 || '',
                shipAddress2: clinic.shippingAddress?.address2 || '',
                shipCity: clinic.shippingAddress?.city || '',
                shipProvince: clinic.shippingAddress?.province || '',
                shipZip: clinic.shippingAddress?.zip || '',
                billAddress1: clinic.billingAddress?.address1 || '',
                billAddress2: clinic.billingAddress?.address2 || '',
                billCity: clinic.billingAddress?.city || '',
                billProvince: clinic.billingAddress?.province || '',
                billZip: clinic.billingAddress?.zip || '',
              });
              setIsEditing(true);
            }
          },
        },
      ]}
    >
      <Layout>
        {/* Stats Cards */}
        <Layout.Section>
          <InlineStack gap="400" wrap={false}>
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued">Total Rentals</Text>
                <Text as="p" variant="headingXl">
                  {statsLoading ? '...' : stats?.totalRentals || 0}
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued">Active Rentals</Text>
                <Text as="p" variant="headingXl">
                  {statsLoading ? '...' : stats?.activeRentals || 0}
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued">Total Revenue</Text>
                <Text as="p" variant="headingXl">
                  {statsLoading ? '...' : `$${(stats?.totalRevenue || 0).toFixed(2)}`}
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued">Outstanding</Text>
                <Text as="p" variant="headingXl">
                  {statsLoading ? '...' : `$${(stats?.outstandingBalance || 0).toFixed(2)}`}
                </Text>
              </BlockStack>
            </Card>
          </InlineStack>
        </Layout.Section>

        {/* Main Content */}
        <Layout.Section>
          <Card padding="0">
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />

            {selectedTab === 0 && (
              <Box padding="400">
                {isEditing ? (
                  <BlockStack gap="400">
                    <TextField
                      label="Name"
                      value={editForm.name}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, name: value }))}
                      autoComplete="off"
                    />
                    <TextField
                      label="Email"
                      type="email"
                      value={editForm.email}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, email: value }))}
                      autoComplete="off"
                    />
                    <TextField
                      label="Phone"
                      type="tel"
                      value={editForm.phone}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, phone: value }))}
                      autoComplete="off"
                    />
                    <Checkbox
                      label="Consignment Clinic"
                      helpText="Clinic keeps equipment on-site and self-fulfills orders."
                      checked={editForm.isConsignmentClinic}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, isConsignmentClinic: value }))}
                    />
                    <Divider />
                    <Text as="h3" variant="headingSm">Shipping Address</Text>
                    <TextField
                      label="Street Address"
                      value={editForm.shipAddress1}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, shipAddress1: value }))}
                      autoComplete="off"
                    />
                    <TextField
                      label="Suite / Unit (optional)"
                      value={editForm.shipAddress2}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, shipAddress2: value }))}
                      autoComplete="off"
                    />
                    <InlineStack gap="400">
                      <Box width="40%">
                        <TextField
                          label="City"
                          value={editForm.shipCity}
                          onChange={(value) => setEditForm((prev) => ({ ...prev, shipCity: value }))}
                          autoComplete="off"
                        />
                      </Box>
                      <Box width="25%">
                        <TextField
                          label="Province"
                          value={editForm.shipProvince}
                          onChange={(value) => setEditForm((prev) => ({ ...prev, shipProvince: value }))}
                          placeholder="ON"
                          autoComplete="off"
                        />
                      </Box>
                      <Box width="25%">
                        <TextField
                          label="Postal Code"
                          value={editForm.shipZip}
                          onChange={(value) => setEditForm((prev) => ({ ...prev, shipZip: value }))}
                          autoComplete="off"
                        />
                      </Box>
                    </InlineStack>
                    <Text as="h3" variant="headingSm">Billing Address</Text>
                    <TextField
                      label="Street Address"
                      value={editForm.billAddress1}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, billAddress1: value }))}
                      autoComplete="off"
                    />
                    <TextField
                      label="Suite / Unit (optional)"
                      value={editForm.billAddress2}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, billAddress2: value }))}
                      autoComplete="off"
                    />
                    <InlineStack gap="400">
                      <Box width="40%">
                        <TextField
                          label="City"
                          value={editForm.billCity}
                          onChange={(value) => setEditForm((prev) => ({ ...prev, billCity: value }))}
                          autoComplete="off"
                        />
                      </Box>
                      <Box width="25%">
                        <TextField
                          label="Province"
                          value={editForm.billProvince}
                          onChange={(value) => setEditForm((prev) => ({ ...prev, billProvince: value }))}
                          placeholder="ON"
                          autoComplete="off"
                        />
                      </Box>
                      <Box width="25%">
                        <TextField
                          label="Postal Code"
                          value={editForm.billZip}
                          onChange={(value) => setEditForm((prev) => ({ ...prev, billZip: value }))}
                          autoComplete="off"
                        />
                      </Box>
                    </InlineStack>
                    <InlineStack gap="200">
                      <Button
                        variant="primary"
                        onClick={() => updateMutation.mutate(editForm)}
                        loading={updateMutation.isPending}
                      >
                        Save Changes
                      </Button>
                      <Button onClick={() => setIsEditing(false)}>Cancel</Button>
                    </InlineStack>
                  </BlockStack>
                ) : (
                  <BlockStack gap="400">
                    <BlockStack gap="200">
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Email</Text>
                        <Text as="span">{clinic.email || 'N/A'}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Phone</Text>
                        <Text as="span">{clinic.phone || 'N/A'}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Billing Terms</Text>
                        <Text as="span">{clinic.billingTerms}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Tax Exempt</Text>
                        <Text as="span">{clinic.taxExempt ? 'Yes' : 'No'}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Consignment Clinic</Text>
                        <Text as="span">{(clinic as any).isConsignmentClinic ? 'Yes' : 'No'}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Created</Text>
                        <Text as="span">{new Date(clinic.createdAt).toLocaleDateString()}</Text>
                      </InlineStack>
                    </BlockStack>

                    <Divider />
                    <BlockStack gap="200">
                      <Text as="p" fontWeight="semibold">Shipping Address</Text>
                      {clinic.shippingAddress?.address1 ? (
                        <>
                          <Text as="p">{clinic.shippingAddress.address1}</Text>
                          {clinic.shippingAddress.address2 && (
                            <Text as="p">{clinic.shippingAddress.address2}</Text>
                          )}
                          <Text as="p">
                            {[
                              clinic.shippingAddress.city,
                              clinic.shippingAddress.province || clinic.shippingAddress.provinceCode,
                            ]
                              .filter(Boolean)
                              .join(', ')}{' '}
                            {clinic.shippingAddress.zip}
                          </Text>
                        </>
                      ) : (
                        <Text as="p" tone="subdued">Not set — click Edit to add</Text>
                      )}
                    </BlockStack>
                    <BlockStack gap="200">
                      <Text as="p" fontWeight="semibold">Billing Address</Text>
                      {clinic.billingAddress?.address1 ? (
                        <>
                          <Text as="p">{clinic.billingAddress.address1}</Text>
                          {clinic.billingAddress.address2 && (
                            <Text as="p">{clinic.billingAddress.address2}</Text>
                          )}
                          <Text as="p">
                            {[
                              clinic.billingAddress.city,
                              clinic.billingAddress.province || clinic.billingAddress.provinceCode,
                            ]
                              .filter(Boolean)
                              .join(', ')}{' '}
                            {clinic.billingAddress.zip}
                          </Text>
                        </>
                      ) : (
                        <Text as="p" tone="subdued">Not set — click Edit to add</Text>
                      )}
                    </BlockStack>
                  </BlockStack>
                )}
              </Box>
            )}

            {selectedTab === 1 && (
              <>
                {rentalsLoading ? (
                  <Box padding="400">
                    <SkeletonBodyText lines={5} />
                  </Box>
                ) : rentalRows.length === 0 ? (
                  <Box padding="800">
                    <Text as="p" tone="subdued" alignment="center">
                      No rentals found for this clinic
                    </Text>
                  </Box>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                    headings={['Order', 'Patient', 'Start', 'End', 'Total', 'Status']}
                    rows={rentalRows}
                  />
                )}
              </>
            )}

            {selectedTab === 2 && (
              <>
                {doctorsLoading ? (
                  <Box padding="400">
                    <SkeletonBodyText lines={5} />
                  </Box>
                ) : doctorRows.length === 0 ? (
                  <Box padding="800">
                    <Text as="p" tone="subdued" alignment="center">
                      No doctors associated with this clinic
                    </Text>
                  </Box>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text']}
                    headings={['Doctor', 'Email', 'Phone', 'Status']}
                    rows={doctorRows}
                  />
                )}
              </>
            )}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
